export * from "./stats.js";
export * from "./lineScore.js";
export * from "./standings.js";
export * from "./power.js";
export * from "./teams.js";
export * from "./schemas.js";
/** How a game's stage reads on screen. */
export const STAGE_LABEL = {
    regular: "Regular season",
    playoff: "Playoffs",
    tournament: "Tournament",
};
/**
 * Stage names for a per-season split, where the plain stage label reads oddly.
 * "Christmas Tournament \u00b7 Tournament" says nothing; inside a tournament the
 * non-bracket games are the round robin.
 */
export const SPLIT_STAGE_LABEL = { tournament: "Round robin" };
/**
 * One competition's rows, split by stage, labelled the way the whole site
 * labels them. The manager profile derives these in the browser so a filter can
 * change what goes into them, and the server derives the same rows from the
 * database — this is the one definition both of them read.
 */
export function splitStageLabel(seasonName, stage) {
    return `${seasonName} \u00b7 ${SPLIT_STAGE_LABEL[stage] ?? STAGE_LABEL[stage] ?? stage}`;
}
/**
 * Order of stages within one competition. The regular season and a round robin
 * both come before that competition's bracket. Note this cannot be an
 * alphabetical sort of the stage string: "playoff" sorts before "regular".
 */
export const STAGE_ORDER = {
    regular: 0,
    tournament: 0,
    playoff: 1,
};
/**
 * Who made a given ban, derived from the pick order.
 *
 * Rev 2.0 bundles the two together: the home manager elects either "ban 1st and
 * 4th, and pick second" or "ban 2nd and 3rd, and pick first". So knowing who
 * picked first is enough to know who banned when, and asking for it separately
 * would let someone record a combination the rules don't allow.
 *
 * Returns null when the pick order isn't recorded, since nothing can be
 * attributed without it.
 */
export function banBelongsTo(banOrder, firstPickManagerId, homeManagerId, awayManagerId) {
    if (firstPickManagerId == null || homeManagerId == null || awayManagerId == null) {
        return null;
    }
    const secondPick = firstPickManagerId === homeManagerId ? awayManagerId : homeManagerId;
    // 1st and 4th belong to whoever picks second; 2nd and 3rd to the first picker.
    return banOrder === 1 || banOrder === 4 ? secondPick : firstPickManagerId;
}
/** How many bans a game has, per Rev 2.0. */
export const BAN_COUNT = 4;
/**
 * Franchises no one was allowed to select, by rules revision.
 *
 * Rev 1.0 walled off the Eagles and the Chiefs for the whole season; Rev 2.0
 * deleted the permanent list and replaced it with the per-game ban phase. This
 * is rule metadata rather than game data, so it lives with the rules instead of
 * in a table — but without it, a franchise nobody was permitted to pick is
 * indistinguishable from one nobody fancied.
 */
export const PERMANENT_BANS = {
    "1.0": ["PHI", "KC"],
    "2.0": [],
};
export function permanentBansFor(rulesRevision) {
    return [...(PERMANENT_BANS[rulesRevision ?? ""] ?? [])];
}
/** Rules constants worth surfacing in the UI, from Rules/Information Rev 2.0. */
export const LEAGUE = {
    name: "Madden Pro League",
    short: "MPL",
    /**
     * The league is run by two Co-Commissioners with identical authority. This is
     * a list, not a single name plus a deputy — Carter was the Schedule Czar and
     * is now a full Co-Commissioner, so the old `commissioner`/`scheduleCzar`
     * pair is gone. First in the list is used where only one name reads naturally.
     */
    commissioners: ["Ian", "Carter"],
    quarterMinutesRegular: 6,
    quarterMinutesPlayoff: 8,
    /** a team may be used this many times per manager, per regular season */
    teamUseLimitRegular: 2,
    teamUseLimitPlayoff: 1,
    /** second team picked must be within this many OVR above the first */
    ovrWindow: 2,
    playoffBracketSize: 8,
    tiebreakers: [
        "Overall record",
        "Head-to-head (or record among tied teams)",
        "Division record",
        "Strength of victory",
        "Opponent win percentage",
        "Random draw",
    ],
};
/**
 * Career (all-time) aggregates only count stat lines from this season number
 * onward.
 *
 * Season 1 and the Christmas Tournament were recorded by hand in a workbook:
 * stat tracking only began in Week 6 of Season 1, whole categories were left
 * blank, and what was captured was captured inconsistently. Adding those
 * partial lines to Season 2's complete ones produces a career passing or
 * rushing total that reads as a real number and isn't one. Scores are the
 * exception — every game ever played has a final score — so points for, points
 * against and win/loss records still count all the way back.
 *
 * Per-season views are untouched: Season 1's own page still reports exactly
 * what the workbook recorded.
 */
export const CAREER_STATS_FROM_SEASON = 2;
/** Why a career stat line stops at Season 2, in one sentence, for the UI. */
export const CAREER_STATS_NOTE = `Career stat totals count Season ${CAREER_STATS_FROM_SEASON} onward. ` +
    "Earlier competitions were entered inconsistently, so only points and " +
    "records carry forward from them — each season's own page still shows " +
    "everything that was recorded for it.";
//# sourceMappingURL=index.js.map